import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { FLOW_ATTRIBUTES, useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { FlowRecordDetailRoute } from "#src/routes/organization/flows/[flowId]/detail/record/[recordId]";
import { DataTable, HeaderType } from "@validereinc/common-components";
import {
  EstimationMethodConfigurationType,
  FlowType,
  FlowV2Type,
  NetworkMeasurementType,
} from "@validereinc/domain";
import { toTitleCaseString, yearMonthName } from "@validereinc/utilities";
import React from "react";

export const useNetworkCalculationFlowHeaders = (): {
  isLoading: boolean;
  headers: Array<HeaderType<FlowType>>;
} => {
  const { localize, isLoading: isLocalizationLoading } = useLocalization();

  const equipmentTypeHeader: HeaderType<FlowType> = {
    key: "equipment_type_id",
    label: `${localize("Equipment")} Type`,
    isSortable: false,
    renderComponent: ({ item }) =>
      // @ts-expect-error TODO: Flow equipment type needs to be updated
      item?.associated_equipment?.type?.name ?? "-",
  };

  const { isLoading: isLoadingFlowHeaders, headers: flowHeadersAll } =
    useFlowHeaders();
  const flowHeaders = flowHeadersAll.filter(
    ({ key }) => key !== FLOW_ATTRIBUTES.FACILITY.key
  );

  const equipmentHeaderIndex =
    flowHeaders.findIndex(({ key }) => key === FLOW_ATTRIBUTES.EQUIPMENT.key) +
    1;

  return {
    isLoading: isLocalizationLoading || isLoadingFlowHeaders,
    headers: [
      ...flowHeaders.slice(0, equipmentHeaderIndex),
      equipmentTypeHeader,
      ...flowHeaders.slice(equipmentHeaderIndex),
    ],
  };
};

export const useNetworkCalculationSourceHeaders = <
  ItemType extends FlowType | FlowV2Type = FlowType,
>(
  configuration?: EstimationMethodConfigurationType,
  measurementType: string = NetworkMeasurementType.volume
): { isLoading: boolean; headers: Array<HeaderType<ItemType>> } => {
  const { getUnitName, isLoading: isLoadingMeasurementTypes } =
    useMeasurementTypes();
  const measurementTypeHeader = toTitleCaseString(measurementType);

  return {
    isLoading: isLoadingMeasurementTypes,
    headers: [
      {
        key: "volume_record_id",
        label: "Source",
        renderComponent: ({ item }: { item: ItemType }) => {
          const input =
            configuration?.asset_input_sources?.[item.id]?.[measurementType];

          if (!input) {
            return "-";
          }

          return (
            <RoutingLink
              to={FlowRecordDetailRoute.toLink({
                pathParams: { flowId: item.id, recordId: input.record_id },
              })}
            >
              {`${item.name} - ${yearMonthName(configuration.year_month)}`}
            </RoutingLink>
          );
        },
      },
      {
        key: "volume_source_type",
        label: "Source Type",
        renderComponent: ({ item }: { item: ItemType }) => {
          const input =
            configuration?.asset_input_sources?.[item.id]?.[measurementType];
          return input ? "Record" : "-";
        },
      },
      {
        key: "volume",
        label: `${measurementTypeHeader} Input`,
        alignment: "right",
        renderComponent: ({ item }: { item: ItemType }) => {
          const input =
            configuration?.asset_inputs?.[item.id]?.[measurementType];

          return (
            <DataTable.DataRow.NumberCell
              value={input?.value}
              unit={input ? getUnitName(input.unit) : ""}
            />
          );
        },
      },
    ],
  };
};
